import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, Badge, Modal, Spinner } from "@toss-ads/ui";
import { listKakaoFormLogs } from "../../lib/api";
import type { KakaoForm } from "../../lib/api";

type StatusMeta = {
  label: string;
  variant: "neutral" | "success" | "danger";
};
const STATUS: Record<string, StatusMeta> = {
  ok: { label: "정상", variant: "success" },
  error: { label: "실패", variant: "danger" },
  skipped: { label: "스킵", variant: "neutral" },
};
function statusMeta(status: string): StatusMeta {
  return STATUS[status] ?? { label: status, variant: "neutral" };
}

// 폼별 폴링 실행 이력. 5분 주기라 초까지 표시해야 구분된다.
export function KakaoFormLogsModal({
  form,
  onClose,
}: {
  form: KakaoForm | null;
  onClose: () => void;
}) {
  const logs = useQuery({
    queryKey: ["admin", "kakao", "forms", form?.id, "logs"],
    queryFn: () => listKakaoFormLogs(form!.id),
    enabled: !!form,
  });

  return (
    <Modal
      open={!!form}
      onClose={onClose}
      width={880}
      title="폴링 로그"
      description={
        form ? `${form.label ?? form.formId} · 최근 50회 (보관 2주)` : undefined
      }
    >
      <div className="max-h-[460px] overflow-auto rounded-lg border border-border">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-surface-muted sticky top-0">
            <tr>
              <Th width={120}>시각</Th>
              <Th width={70}>결과</Th>
              <Th width={56}>페이지</Th>
              <Th width={56}>받음</Th>
              <Th width={56}>신규</Th>
              <Th width={56}>중복</Th>
              <Th width={56}>실패</Th>
              <Th width={64}>소요</Th>
              <Th>에러</Th>
            </tr>
          </thead>
          <tbody>
            {logs.isLoading && (
              <tr>
                <td colSpan={9} className="py-10">
                  <div className="flex justify-center text-ink-subtle">
                    <Spinner size={18} />
                  </div>
                </td>
              </tr>
            )}
            {logs.isError && (
              <tr>
                <td colSpan={9} className="py-12 text-center text-[13px] text-danger">
                  {logs.error instanceof ApiError
                    ? logs.error.message
                    : "로그를 불러오지 못했습니다."}
                </td>
              </tr>
            )}
            {!logs.isLoading && !logs.isError && (logs.data ?? []).length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="py-10 text-center text-[13px] text-ink-subtle"
                >
                  아직 폴링 기록이 없습니다.
                </td>
              </tr>
            )}
            {(logs.data ?? []).map((l) => (
              <tr key={l.id} className="border-t border-border">
                <Td className="font-mono text-[11px] text-ink-muted">
                  {formatTime(l.createdAt)}
                </Td>
                <Td>
                  <Badge variant={statusMeta(l.status).variant} dot>
                    {statusMeta(l.status).label}
                  </Badge>
                </Td>
                <Td className="text-ink-muted">{l.pages}</Td>
                <Td className="text-ink-muted">{l.fetched}</Td>
                <Td className={l.inserted > 0 ? "text-ink font-semibold" : "text-ink-subtle"}>
                  {l.inserted}
                </Td>
                <Td className="text-ink-subtle">{l.duplicated}</Td>
                <Td className={l.failed > 0 ? "text-danger" : "text-ink-subtle"}>
                  {l.failed}
                </Td>
                <Td className="text-ink-subtle text-[11px]">
                  {l.durationMs == null ? "—" : `${(l.durationMs / 1000).toFixed(1)}s`}
                </Td>
                <Td className="text-[11px] text-danger">
                  <span className="block max-w-[260px] truncate" title={l.error ?? ""}>
                    {l.error ?? ""}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function Th({ children, width }: { children: React.ReactNode; width?: number }) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-2.5 py-2 align-middle ${className ?? ""}`}>{children}</td>;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${m}.${day} ${hh}:${mm}:${ss}`;
}
